/**
 * Admin QA dashboard for live-chat ratings.
 *
 * Sits at the top of the Live Chat tab so admins can monitor patient
 * satisfaction without leaving the workflow. Three states:
 *   1. **Collapsed** (default) — single summary chip showing avg + total count
 *   2. **Expanded** — full ratings list with star filter, transcript drill-in
 *   3. **Transcript** — modal-ish inline expansion of a single chat for QA
 *
 * Uses the existing chat audit collection (`db.chat_conversations`) — no new
 * tables needed. Read-only; QA reviewers can't edit or delete patient ratings,
 * preserving the integrity of the feedback signal.
 */
import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Star, ChevronDown, ChevronUp, MessageSquare, RefreshCw, FileText, X, Bell, Save, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";

const STAR_FILTERS = [
  { label: "All", min: 1, max: 5 },
  { label: "Low (1-2★)", min: 1, max: 2 },
  { label: "Mid (3★)", min: 3, max: 3 },
  { label: "High (4-5★)", min: 4, max: 5 },
];

function StarRow({ rating }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-3.5 h-3.5 ${n <= rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function ChatRatingsCard() {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(STAR_FILTERS[0]);
  // Inline transcript expansion — null when no transcript is open.
  const [transcriptConvId, setTranscriptConvId] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  // Low-rating alert config: threshold + recipients. Lazy-loaded with the
  // ratings list to keep first-paint fast.
  const [alertCfg, setAlertCfg] = useState(null);
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertExpanded, setAlertExpanded] = useState(false);
  // Recovery-rate KPI (rolling 30d) — `recovery_rate = resolved/alerted`.
  // Loaded alongside the ratings list so it stays in sync.
  const [recovery, setRecovery] = useState(null);
  // Filter: show only unresolved low-rating alerts (manager triage queue).
  const [unresolvedOnly, setUnresolvedOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: resp }, { data: cfg }, { data: rec }] = await Promise.all([
        API.get(`/chat/admin/ratings?min_rating=${filter.min}&max_rating=${filter.max}&limit=100`),
        API.get(`/chat/admin/low-rating-alert`).catch(() => ({ data: { enabled: true, threshold: 2, recipients: [] } })),
        API.get(`/chat/admin/ratings/recovery-stats`).catch(() => ({ data: null })),
      ]);
      setData(resp);
      setAlertCfg(cfg);
      setRecovery(rec);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load ratings");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const saveAlertCfg = async () => {
    if (!alertCfg) return;
    setAlertSaving(true);
    try {
      const { data } = await API.put("/chat/admin/low-rating-alert", {
        enabled: alertCfg.enabled,
        threshold: alertCfg.threshold,
        // Backend re-parses comma-separated strings too — be lenient.
        recipients: alertCfg.recipients,
      });
      setAlertCfg(data);
      toast.success("Alert settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    } finally {
      setAlertSaving(false);
    }
  };

  const [digestSending, setDigestSending] = useState(false);
  const sendDigestNow = async () => {
    if (!(await brandedConfirm({ title: 'Are you sure?', description: "Send the weekly recovery digest to the configured recipients right now?", confirmText: 'Confirm', cancelText: 'Cancel', tone: 'info' }))) return;
    setDigestSending(true);
    try {
      const { data } = await API.post("/chat/admin/ratings/recovery-digest/send-now");
      const n = (data.last_digest_sent_to || []).length;
      if (n === 0) {
        toast.error("No recipients configured — add at least one email above first");
      } else {
        toast.success(`Recovery digest sent to ${n} recipient${n === 1 ? "" : "s"}`);
      }
      // Refresh so the card shows the new "last digest" timestamp.
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send digest");
    } finally {
      setDigestSending(false);
    }
  };

  useEffect(() => {
    // Lazy-load — only fetch ratings once the admin actually expands the card.
    if (expanded) load();
  }, [expanded, load]);

  const openTranscript = async (cid) => {
    setTranscriptConvId(cid);
    setLoadingTranscript(true);
    setTranscript(null);
    try {
      const { data: t } = await API.get(`/chat/admin/ratings/${cid}/transcript`);
      setTranscript(t);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load transcript");
      setTranscriptConvId(null);
    } finally {
      setLoadingTranscript(false);
    }
  };

  const summary = data?.summary;
  const ratings = (data?.ratings || []).filter((r) => {
    if (!unresolvedOnly) return true;
    // "Unresolved only" surfaces chats that fired a low-rating alert and
    // haven't been marked resolved yet — i.e. the actual triage queue.
    return r.low_rating_alerted_at && r.resolution_status !== "resolved";
  });

  return (
    <Card className="border-purple-200 dark:border-purple-900/40 mb-4" data-testid="chat-ratings-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white">
            <Star className="w-4 h-4 fill-white" />
          </span>
          Chat Ratings (QA Review)
          {summary && summary.total > 0 && (
            <span className="ml-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-950/40 rounded-full px-2.5 py-0.5">
              <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
              {summary.average} · {summary.total} rated
            </span>
          )}
          {recovery && recovery.alerted > 0 && (
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-0.5 ${
                recovery.recovery_rate >= 80
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : recovery.recovery_rate >= 50
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                  : "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
              }`}
              title={`${recovery.resolved}/${recovery.alerted} low-rated chats resolved in the last 30 days`}
              data-testid="chat-ratings-recovery-chip"
            >
              Recovery {recovery.recovery_rate ?? "—"}% · {recovery.open} open
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto h-7 px-2"
            onClick={() => setExpanded((e) => !e)}
            data-testid="chat-ratings-toggle"
          >
            {expanded ? <><ChevronUp className="w-3.5 h-3.5 mr-1" /> Hide</> : <><ChevronDown className="w-3.5 h-3.5 mr-1" /> Review</>}
          </Button>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {STAR_FILTERS.map((f) => (
              <Button
                key={f.label}
                size="sm"
                variant={filter.label === f.label ? "default" : "outline"}
                onClick={() => setFilter(f)}
                className={`h-7 text-xs ${filter.label === f.label ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}`}
                data-testid={`chat-ratings-filter-${f.label}`}
              >
                {f.label}
              </Button>
            ))}
            <Button
              size="sm"
              variant={unresolvedOnly ? "default" : "outline"}
              onClick={() => setUnresolvedOnly((v) => !v)}
              className={`h-7 text-xs ${unresolvedOnly ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`}
              data-testid="chat-ratings-unresolved-toggle"
            >
              Unresolved only
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={load}
              disabled={loading}
              className="h-7 px-2 ml-auto"
              data-testid="chat-ratings-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {summary && summary.total > 0 && (
            <div className="grid grid-cols-5 gap-1.5 text-center" data-testid="chat-ratings-distribution">
              {[1, 2, 3, 4, 5].map((star) => {
                const count = summary.by_rating[star] || 0;
                const pct = summary.total > 0 ? (count / summary.total) * 100 : 0;
                return (
                  <div key={star} className="bg-slate-50 dark:bg-slate-800/40 rounded-lg p-2">
                    <div className="flex items-center justify-center gap-0.5 mb-0.5">
                      <span className="text-[10px] font-bold text-slate-600">{star}</span>
                      <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
                    </div>
                    <p className="text-base font-bold text-slate-900 dark:text-white">{count}</p>
                    <p className="text-[9px] text-slate-400">{Math.round(pct)}%</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Low-rating auto-alert config — collapsible to keep the QA grid clean. */}
          {alertCfg && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-950/20 px-3 py-2.5" data-testid="chat-ratings-alert-config">
              <button
                type="button"
                onClick={() => setAlertExpanded((e) => !e)}
                className="w-full flex items-center gap-2 text-left"
                data-testid="chat-ratings-alert-toggle"
              >
                <Bell className="w-3.5 h-3.5 text-amber-700" />
                <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                  Low-rating auto-alert
                </span>
                <span className="text-[10px] text-amber-700 dark:text-amber-300">
                  {alertCfg.enabled && alertCfg.recipients.length > 0
                    ? `On · ≤${alertCfg.threshold}★ → ${alertCfg.recipients.length} recipient${alertCfg.recipients.length === 1 ? "" : "s"}`
                    : alertCfg.enabled
                    ? "On · no recipients configured"
                    : "Disabled"}
                </span>
                <span className="ml-auto text-[10px] text-amber-700">
                  {alertExpanded ? <ChevronUp className="w-3.5 h-3.5 inline" /> : <ChevronDown className="w-3.5 h-3.5 inline" />}
                </span>
              </button>
              {alertExpanded && (
                <div className="mt-3 space-y-2.5">
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={alertCfg.enabled}
                        onChange={(e) => setAlertCfg({ ...alertCfg, enabled: e.target.checked })}
                        data-testid="chat-ratings-alert-enabled"
                      />
                      Enabled
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300">
                      Trigger at
                      <select
                        value={alertCfg.threshold}
                        onChange={(e) => setAlertCfg({ ...alertCfg, threshold: Number(e.target.value) })}
                        className="border border-slate-200 rounded-md text-xs px-2 py-1 bg-white dark:bg-slate-900"
                        data-testid="chat-ratings-alert-threshold"
                      >
                        <option value={1}>≤ 1★ (worst only)</option>
                        <option value={2}>≤ 2★ (recommended)</option>
                        <option value={3}>≤ 3★ (aggressive)</option>
                      </select>
                      or below
                    </label>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Email recipients
                    </label>
                    <input
                      type="text"
                      value={(alertCfg.recipients || []).join(", ")}
                      onChange={(e) =>
                        setAlertCfg({
                          ...alertCfg,
                          recipients: e.target.value
                            .split(",")
                            .map((r) => r.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder="manager@glpflo.org, qa@glpflo.org"
                      className="mt-1 w-full text-xs rounded-md border border-slate-200 px-3 py-1.5 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                      data-testid="chat-ratings-alert-recipients"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      Comma-separated. Up to 20. Each gets an email within seconds of a triggering rating with the patient name, category, comment, and a transcript link.
                    </p>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    {alertCfg.updated_at && (
                      <span className="text-[10px] text-slate-400 mr-auto italic">
                        Last updated {new Date(alertCfg.updated_at).toLocaleString()}
                        {alertCfg.updated_by && ` by ${alertCfg.updated_by}`}
                      </span>
                    )}
                    <Button
                      size="sm"
                      onClick={saveAlertCfg}
                      disabled={alertSaving}
                      className="h-7 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                      data-testid="chat-ratings-alert-save"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      {alertSaving ? "Saving…" : "Save settings"}
                    </Button>
                  </div>

                  {/* Weekly recovery digest — same recipients as the auto-alert. */}
                  <div className="mt-2 pt-3 border-t border-amber-200/70 dark:border-amber-900/40">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[180px]">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Weekly recovery digest
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed mt-0.5">
                          Auto-emails every Monday 07:30 UTC: KPIs, 8-wk trend, top resolution actions, stalled queue.
                          {alertCfg.last_digest_at && (
                            <span className="block text-[10px] text-emerald-600 mt-0.5">
                              Last sent {new Date(alertCfg.last_digest_at).toLocaleString()}
                              {alertCfg.last_digest_summary &&
                                ` · ${alertCfg.last_digest_summary.resolved}/${alertCfg.last_digest_summary.alerted} resolved (${alertCfg.last_digest_summary.recovery_rate ?? "—"}%)`}
                            </span>
                          )}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={sendDigestNow}
                        disabled={digestSending || !(alertCfg.recipients || []).length}
                        className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                        data-testid="chat-ratings-digest-send-now"
                        title={(alertCfg.recipients || []).length ? "" : "Add at least one recipient above first"}
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        {digestSending ? "Sending…" : "Send digest now"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <p className="text-xs text-slate-400 text-center py-6">Loading ratings…</p>
          ) : ratings.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6" data-testid="chat-ratings-empty">
              No rated chats in this filter yet.
            </p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="chat-ratings-list">
              {ratings.map((r) => (
                <div
                  key={r.id}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3"
                  data-testid={`chat-rating-row-${r.id}`}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <StarRow rating={r.rating || 0} />
                    <span className="text-xs font-semibold text-slate-900 dark:text-white">{r.patient_name || "Patient"}</span>
                    <span className="text-[11px] text-slate-400">{r.category}</span>
                    <span className="text-[11px] text-slate-400">closed by {r.closed_by || "—"}</span>
                    {r.low_rating_alerted_at && (
                      r.resolution_status === "resolved" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 rounded-full px-2 py-0.5">
                          <CheckCircle2 className="w-2.5 h-2.5" /> Resolved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 rounded-full px-2 py-0.5">
                          ● Open
                        </span>
                      )
                    )}
                    <span className="text-[11px] text-slate-400 ml-auto">
                      {new Date(r.rating_submitted_at).toLocaleString()}
                    </span>
                  </div>
                  {r.rating_comment && (
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-2 italic leading-relaxed border-l-2 border-purple-300 pl-2">
                      &quot;{r.rating_comment}&quot;
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[11px] text-purple-600 hover:text-purple-700"
                      onClick={() => openTranscript(r.id)}
                      data-testid={`chat-rating-transcript-${r.id}`}
                    >
                      <FileText className="w-3 h-3 mr-1" /> View transcript
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Inline transcript drawer */}
          {transcriptConvId && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setTranscriptConvId(null)}>
              <div
                className="bg-white dark:bg-slate-900 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="chat-ratings-transcript-modal"
              >
                <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
                  <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-purple-600" />
                    Chat transcript
                  </h3>
                  <Button size="sm" variant="ghost" onClick={() => setTranscriptConvId(null)} className="h-7 w-7 p-0">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50 dark:bg-slate-950">
                  {loadingTranscript ? (
                    <p className="text-xs text-slate-400 text-center py-6">Loading…</p>
                  ) : transcript ? (
                    <>
                      <div className="mb-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <StarRow rating={transcript.conversation?.rating || 0} />
                          <span className="text-xs font-semibold">{transcript.conversation?.patient_name || "Patient"}</span>
                          <span className="text-[11px] text-slate-400">{transcript.conversation?.category}</span>
                        </div>
                        {transcript.conversation?.rating_comment && (
                          <p className="text-xs text-slate-600 italic mt-1">&quot;{transcript.conversation.rating_comment}&quot;</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">
                          Closed by {transcript.conversation?.closed_by || "—"} at{" "}
                          {transcript.conversation?.closed_at
                            ? new Date(transcript.conversation.closed_at).toLocaleString()
                            : "—"}
                          {transcript.conversation?.consent_acknowledged && (
                            <span className="ml-2 text-emerald-600">· QA consent acknowledged</span>
                          )}
                        </p>
                      </div>
                      {(transcript.messages || []).map((m) => {
                        const isStaff = m.sender_role !== "patient";
                        return (
                          <div key={m.id} className={`flex ${isStaff ? "justify-start" : "justify-end"}`}>
                            <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${isStaff ? "bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700" : "bg-purple-600 text-white"}`}>
                              {isStaff && (
                                <p className="text-[10px] font-semibold text-purple-600 mb-0.5">
                                  {m.sender_name} <span className="text-slate-400 font-normal">· {m.sender_role}</span>
                                </p>
                              )}
                              <p className="text-sm leading-relaxed">{m.message}</p>
                              <p className={`text-[10px] mt-1 ${isStaff ? "text-slate-400" : "text-purple-200"}`}>
                                {new Date(m.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <p className="text-xs text-slate-400 text-center py-6">Transcript not available</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
